<template>
  <VDialog v-model="isVisible" persistent>
    <template #default="{ isActive }">
      <VCard title="Database Diff Results">
        <VCardText v-debug>
          <h3>dialogData</h3>
          {{ dialogData }}
        </VCardText>
        <VCardText v-if="dialogData">
          <VAlert
            v-if="error"
            type="error"
            density="compact"
            prominent
            border="start"
            class="mb-6 rounded-lg"
          >
            <strong>Error!</strong> {{ error }}
          </VAlert>

          <div v-if="diffData">
            <VDivider class="my-6"></VDivider>
            <VCardSubtitle class="text-h5 font-weight-bold text-grey-darken-2">
              Diff Results:
            </VCardSubtitle>

            <div
              v-for="(changes, tableName) in diffData"
              :key="tableName"
              class="mb-8 pa-4 rounded-lg bg-grey-lighten-4"
            >
              <h3 class="text-h6 font-weight-semibold text-capitalize mb-4">
                Table: {{ tableName }}
              </h3>

              <!-- Combined Table with Copy to Excel Button -->
              <div v-if="getCombinedTableData(changes).length > 0">
                <div class="mb-4 d-flex justify-space-between align-center">
                  <div>
                    <VChip
                      class="mr-2"
                      color="green"
                      variant="outlined"
                      size="small"
                    >
                      <VIcon start size="small">mdi-plus-circle</VIcon>
                      Added: {{ changes.added?.length || 0 }}
                    </VChip>
                    <VChip
                      class="mr-2"
                      color="red"
                      variant="outlined"
                      size="small"
                    >
                      <VIcon start size="small">mdi-minus-circle</VIcon>
                      Deleted: {{ changes.deleted?.length || 0 }}
                    </VChip>
                    <VChip color="blue" variant="outlined" size="small">
                      <VIcon start size="small">mdi-pencil</VIcon>
                      Updated: {{ changes.updated?.length || 0 }}
                    </VChip>
                  </div>
                  <VBtn
                    color="success"
                    size="small"
                    :loading="copyingTable"
                    @click="copyTableToClipboard(tableName, changes)"
                  >
                    <VIcon start size="small">mdi-content-copy</VIcon>
                    Copy Table for Excel
                  </VBtn>
                </div>

                <div :id="`table-${tableName}`" class="diff-table-container">
                  <table class="diff-table combined-table">
                    <thead>
                      <tr>
                        <th
                          style="
                          background-color: #f5f5f5;
                          font-weight: bold;
                          min-width: 100px;
                        "
                        >
                          VALUE TYPE
                        </th>
                        <th
                          v-for="column in getAllColumnsForTable(changes)"
                          :key="column"
                        >
                          {{ column }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, index) in getCombinedTableData(changes)"
                        :key="`${row.type}-${index}`"
                        :class="getRowClass(row.type)"
                        @click="copyRowToClipboard(row)"
                      >
                        <td
                          class="value-type-cell"
                          :class="getValueTypeClass(row.type)"
                        >
                          <VIcon
                            start
                            size="small"
                            :color="getValueTypeIconColor(row.type)"
                          >
                            {{ getValueTypeIcon(row.type) }}
                          </VIcon>
                          {{ row.type.toUpperCase() }}
                        </td>
                        <td
                          v-for="column in getAllColumnsForTable(changes)"
                          :key="column"
                          :class="getCellClass(row, column)"
                        >
                          {{
                            formatValueForDisplay(getValueForColumn(row, column))
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- No Changes Panel -->
              <div v-else class="text-center pa-3">
                <VIcon size="large" color="grey">mdi-check-all</VIcon>
                <p class="text-h6 text-grey-darken-1 mt-2">No Changes Detected</p>
                <p class="text-grey">No changes detected for this table.</p>
              </div>
            </div>
          </div>
        </VCardText>
        <VCardText v-debug>
          <h3>outputData</h3>
          {{ outputData }}
        </VCardText>
        <VCardActions>
          <VSpacer></VSpacer>
          <!-- <VBtn
            color="blue lighten"
            style="color: white"
            :disabled="
              outputData?.checkList?.length !== dialogData?.checkList?.length
            "
            @click="emitOk"
          >
            A
          </VBtn> -->
          <VBtn variant="text" @click="emitCancel"> CLOSE </VBtn>
        </VCardActions>
      </VCard>
    </template>
  </VDialog>
</template>

<script>
import DialogMixin from "@/mixins/DialogMixin";
export default {
  mixins: [DialogMixin],
  data() {
    return {
      diffData: null, // State to store the diff data
      loading: false, // State for loading indicator
      error: null, // State for general error messages
      pastedJson: "", // State for the pasted JSON string
      pasteError: null, // State for paste JSON parsing errors
      copyingTable: false, // Loading state for copy to Excel functionality
      snackbar: {
        // State for snackbar notifications
        show: false,
        message: "",
        color: "success",
        timeout: 3000,
      },
    };
  },
  watch: {
    // Watcher to automatically fetch diff data when dialog opens
    dialogData(newVal) {
      this.setDiffDataAndExpand(newVal);
    },
    // dirty fix to port old notification system into new notification system
    snackbar: {
      deep: true,
      handler(newVal) {
        if (newVal.show) {
          this.$dialog.notify[newVal.color](newVal.message);
          setTimeout(() => {
            this.snackbar.show = false;
          }, newVal.timeout || 1000);
        }
      },
    },
  },
  methods: {
    // Combined table methods
    getAllColumnsForTable(changes) {
      const allColumns = [];
      const seenColumns = new Set();

      // Get columns from added records (maintain original order)
      if (changes.added && changes.added.length > 0) {
        Object.keys(changes.added[0]).forEach((key) => {
          if (!seenColumns.has(key)) {
            allColumns.push(key);
            seenColumns.add(key);
          }
        });
      }

      // Get columns from deleted records
      if (changes.deleted && changes.deleted.length > 0) {
        Object.keys(changes.deleted[0]).forEach((key) => {
          if (!seenColumns.has(key)) {
            allColumns.push(key);
            seenColumns.add(key);
          }
        });
      }

      // Get columns from updated records (both old and new)
      if (changes.updated && changes.updated.length > 0) {
        Object.keys(changes.updated[0].old).forEach((key) => {
          if (!seenColumns.has(key)) {
            allColumns.push(key);
            seenColumns.add(key);
          }
        });
        Object.keys(changes.updated[0].new).forEach((key) => {
          if (!seenColumns.has(key)) {
            allColumns.push(key);
            seenColumns.add(key);
          }
        });
      }

      return allColumns;
    },

    getCombinedTableData(changes) {
      const rows = [];

      // Add "added" records
      if (changes.added && changes.added.length > 0) {
        changes.added.forEach((record) => {
          rows.push({
            type: "added",
            data: record,
            original: record,
          });
        });
      }

      // Add "deleted" records
      if (changes.deleted && changes.deleted.length > 0) {
        changes.deleted.forEach((record) => {
          rows.push({
            type: "deleted",
            data: record,
            original: record,
          });
        });
      }

      // Add "updated" records (both old and new)
      if (changes.updated && changes.updated.length > 0) {
        changes.updated.forEach((record) => {
          // Add old version
          rows.push({
            type: "updated-old",
            data: record.old,
            original: record,
            changes: record.changes,
          });
          // Add new version
          rows.push({
            type: "updated-new",
            data: record.new,
            original: record,
            changes: record.changes,
          });
        });
      }

      return rows;
    },

    getValueForColumn(row, column) {
      return Object.prototype.hasOwnProperty.call(row.data, column)
        ? row.data[column]
        : "";
    },

    getRowClass(type) {
      return `row-${type}`;
    },

    getValueTypeClass(type) {
      return `value-type-${type}`;
    },

    getValueTypeIcon(type) {
      switch (type) {
        case "added":
          return "mdi-plus-circle";
        case "deleted":
          return "mdi-minus-circle";
        case "updated-old":
          return "mdi-pencil-minus";
        case "updated-new":
          return "mdi-pencil-plus";
        default:
          return "mdi-circle";
      }
    },

    getValueTypeIconColor(type) {
      switch (type) {
        case "added":
          return "green darken-2";
        case "deleted":
          return "red darken-2";
        case "updated-old":
          return "orange darken-2";
        case "updated-new":
          return "blue darken-2";
        default:
          return "grey";
      }
    },

    getCellClass(row, column) {
      // Highlight changed fields in updated records
      if (
        (row.type === "updated-old" || row.type === "updated-new") &&
        row.changes &&
        Object.prototype.hasOwnProperty.call(row.changes, column)
      ) {
        return "cell-changed";
      }
      return "";
    },

    async copyTableToClipboard(tableName, changes) {
      this.copyingTable = true;
      try {
        const columns = this.getAllColumnsForTable(changes);
        const rows = this.getCombinedTableData(changes);

        // Create HTML table with styling for Excel
        let htmlContent = `<table border="1" style="border-collapse: collapse; font-family: Arial, sans-serif; font-size: 11pt;">`;

        // Header row
        htmlContent += "<tr>";
        htmlContent +=
          '<th style="background-color: #f5f5f5; font-weight: bold; padding: 8px; border: 1px solid #ccc;">VALUE TYPE</th>';
        columns.forEach((column) => {
          htmlContent += `<th style="background-color: #f5f5f5; font-weight: bold; padding: 8px; border: 1px solid #ccc;">${column}</th>`;
        });
        htmlContent += "</tr>";

        // Data rows with styling based on type
        rows.forEach((row) => {
          const valueType = row.type.toUpperCase().replace("-", " ");
          let rowBgColor = "#ffffff";
          let typeColor = "#000000";
          let typeBgColor = "#f5f5f5";

          // Set colors based on type
          switch (row.type) {
            case "added":
              rowBgColor = "#f1f8e9";
              typeColor = "#2e7d32";
              typeBgColor = "#e8f5e9";
              break;
            case "deleted":
              rowBgColor = "#fce4ec";
              typeColor = "#c62828";
              typeBgColor = "#ffebee";
              break;
            case "updated-old":
              rowBgColor = "#fff8e1";
              typeColor = "#ef6c00";
              typeBgColor = "#fff3e0";
              break;
            case "updated-new":
              rowBgColor = "#e1f5fe";
              typeColor = "#1976d2";
              typeBgColor = "#e3f2fd";
              break;
          }

          htmlContent += `<tr style="background-color: ${rowBgColor};">`;
          htmlContent += `<td style="background-color: ${typeBgColor}; color: ${typeColor}; font-weight: bold; text-align: center; padding: 8px; border: 1px solid #ccc;">${valueType}</td>`;

          columns.forEach((column) => {
            let value = this.getValueForColumn(row, column);
            if (typeof value === "object" && value !== null) {
              value = JSON.stringify(value);
            }

            let cellStyle = `padding: 8px; border: 1px solid #ccc;`;

            // Highlight changed fields in updated records
            if (
              (row.type === "updated-old" || row.type === "updated-new") &&
              row.changes &&
              Object.prototype.hasOwnProperty.call(row.changes, column)
            ) {
              cellStyle +=
                " background-color: #fff59d; font-weight: bold; border: 2px solid #fbc02d;";
            }

            const cellValue = String(
              value !== null && value !== undefined ? value : ""
            );
            htmlContent += `<td style="${cellStyle}">${cellValue}</td>`;
          });

          htmlContent += "</tr>";
        });

        htmlContent += "</table>";

        // Try to copy HTML content using DOM selection (more compatible)
        try {
          // Create a temporary div with the HTML content
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = htmlContent;
          tempDiv.style.position = "fixed";
          tempDiv.style.left = "-9999px";
          tempDiv.style.top = "-9999px";
          document.body.appendChild(tempDiv);

          // Select the content
          const range = document.createRange();
          range.selectNodeContents(tempDiv);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);

          // Try to copy
          const successful = document.execCommand("copy");
          document.body.removeChild(tempDiv);
          selection.removeAllRanges();

          if (successful) {
            this.snackbar.message = `Table "${tableName}" copied with formatting! Paste into Excel to see colors and styles.`;
            this.snackbar.color = "success";
            this.snackbar.show = true;
            return;
          } else {
            throw new Error("DOM copy failed");
          }
        } catch (domError) {
          console.log(
            "DOM copy failed, trying modern clipboard API:",
            domError
          );

          // Try modern clipboard API if available
          if (navigator.clipboard && navigator.clipboard.writeText) {
            const plainTextContent = this.createPlainTextTable(
              tableName,
              changes
            );
            await navigator.clipboard.writeText(plainTextContent);
            this.snackbar.message = `Table "${tableName}" copied as plain text. Paste into Excel.`;
            this.snackbar.color = "info";
            this.snackbar.show = true;
            return;
          } else {
            throw new Error("No clipboard method available");
          }
        }
      } catch (error) {
        console.error("Failed to copy table:", error);

        // Final fallback: plain text with textarea
        try {
          const plainTextContent = this.createPlainTextTable(
            tableName,
            changes
          );
          const tempTextArea = document.createElement("textarea");
          tempTextArea.value = plainTextContent;
          document.body.appendChild(tempTextArea);
          tempTextArea.select();
          tempTextArea.setSelectionRange(0, 99999);

          const successful = document.execCommand("copy");
          document.body.removeChild(tempTextArea);

          if (successful) {
            this.snackbar.message = `Table "${tableName}" copied as plain text. Paste into Excel.`;
            this.snackbar.color = "info";
          } else {
            this.snackbar.message =
              "Failed to copy table. Please manually select and copy the table from the page.";
            this.snackbar.color = "error";
          }
        } catch (finalError) {
          this.snackbar.message =
            "Failed to copy table. Please manually select and copy the table from the page.";
          this.snackbar.color = "error";
        }

        this.snackbar.show = true;
      } finally {
        this.copyingTable = false;
      }
    },

    createPlainTextTable(tableName, changes) {
      const columns = this.getAllColumnsForTable(changes);
      const rows = this.getCombinedTableData(changes);

      // Create plain text version
      let csvContent = "VALUE TYPE\t" + columns.join("\t") + "\n";

      rows.forEach((row) => {
        const valueType = row.type.toUpperCase().replace("-", " ");
        const rowData = columns.map((column) => {
          let value = this.getValueForColumn(row, column);
          if (typeof value === "object" && value !== null) {
            value = JSON.stringify(value);
          }
          return String(value !== null && value !== undefined ? value : "")
            .replace(/\t/g, " ")
            .replace(/\n/g, " ");
        });

        csvContent += valueType + "\t" + rowData.join("\t") + "\n";
      });

      return csvContent;
    },

    copyRowToClipboard(row) {
      const textToCopy = JSON.stringify(row.original, null, 2);

      // Use a temporary textarea to copy text (cross-browser compatible)
      const tempTextArea = document.createElement("textarea");
      tempTextArea.value = textToCopy;
      document.body.appendChild(tempTextArea);
      tempTextArea.select();

      try {
        const successful = document.execCommand("copy");
        if (successful) {
          this.snackbar.message = "Row data copied to clipboard!";
          this.snackbar.color = "success";
        } else {
          this.snackbar.message = "Failed to copy row data.";
          this.snackbar.color = "error";
        }
      } catch (err) {
        this.snackbar.message = "Error copying row data: " + err.message;
        this.snackbar.color = "error";
        console.error("Copy command failed:", err);
      } finally {
        document.body.removeChild(tempTextArea);
        this.snackbar.show = true;
      }
    },

    // Helper to format values for display, especially objects
    formatValueForDisplay(value) {
      if (typeof value === "object" && value !== null) {
        return JSON.stringify(value);
      }
      return value;
    },

    // Function to set diffData and initialize panel states
    setDiffDataAndExpand(data) {
      this.diffData = data;
      this.error = null; // Clear any previous general errors
      this.pasteError = null; // Clear any previous paste errors
    },

    // Function to simulate fetching database diff data
    async getDatabaseDiff() {
      this.loading = true;
      this.error = null;
      this.pastedJson = ""; // Clear pasted data when simulating
      this.pasteError = null;

      // Simulate an API call delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      try {
        // Mock data that would typically come from your Laravel DatabaseDiffer
        const mockDiffData = {
          users: {
            added: [
              {
                id: 4,
                name: "Alice",
                email: "alice@example.com",
                status: "active",
                address_line1: "123 Main St",
                address_line2: "Apt 4B",
                city: "Anytown",
                zip_code: "12345",
                phone_number: "555-1234",
                registration_date: "2025-06-20",
                last_login: "2025-06-27 10:00:00",
                preferences: { newsletter: true, sms_alerts: false },
                notes: "New user from marketing campaign, needs follow-up.",
                api_key:
                  "some_long_api_key_string_for_testing_purposes_1234567890",
                subscription_level: "premium",
              },
            ],
            deleted: [
              {
                id: 2,
                name: "Bob",
                email: "bob@example.com",
                status: "inactive",
                address_line1: "456 Oak Ave",
                address_line2: "",
                city: "Otherville",
                zip_code: "67890",
                phone_number: "555-5678",
                registration_date: "2023-01-01",
                last_login: "2024-05-10 14:00:00",
                preferences: { newsletter: false, sms_alerts: true },
                notes: "Account closed due to inactivity.",
                api_key: "another_long_api_key_string_for_testing_0987654321",
                subscription_level: "free",
              },
            ],
            updated: [
              {
                id: 1,
                old: {
                  name: "John",
                  email: "john@example.com",
                  status: "active",
                  created_at: "2024-01-01 10:00:00",
                  updated_at: "2024-01-01 10:00:00",
                  address_line1: "100 Old Rd",
                  city: "Springfield",
                  phone_number: "555-0001",
                  preferences: { newsletter: true, sms_alerts: false },
                  last_login: "2025-06-27 10:00:00",
                },
                new: {
                  name: "Jonathan Doe",
                  email: "jonathan.doe@example.com",
                  status: "active",
                  created_at: "2024-01-01 10:00:00",
                  updated_at: "2025-06-28 15:30:00",
                  address_line1: "200 New Blvd",
                  city: "Springfield",
                  phone_number: "555-0001",
                  preferences: { newsletter: true, sms_alerts: true },
                  last_login: "2025-06-28 15:30:00",
                },
                changes: {
                  name: "Jonathan Doe",
                  email: "jonathan.doe@example.com",
                  address_line1: "200 New Blvd",
                  preferences: { sms_alerts: true },
                  last_login: "2025-06-28 15:30:00",
                },
              },
              {
                id: 3,
                old: {
                  name: "Charlie",
                  email: "charlie@example.com",
                  status: "pending",
                  created_at: "2024-02-15 08:00:00",
                  updated_at: "2024-02-15 08:00:00",
                  notes: "Waiting for approval.",
                },
                new: {
                  name: "Charlie",
                  email: "charlie@example.com",
                  status: "approved",
                  created_at: "2024-02-15 08:00:00",
                  updated_at: "2025-06-28 15:35:00",
                  notes: "Approved by admin.",
                },
                changes: { status: "approved", notes: "Approved by admin." },
              },
            ],
          },
          orders: {
            added: [
              {
                id: 105,
                user_id: 4,
                amount: 150.0,
                status: "new",
                created_at: "2025-06-28 15:40:00",
                updated_at: "2025-06-28 15:40:00",
                shipping_address: "456 Main St",
                payment_method: "Credit Card",
                tracking_number: "ABC123XYZ",
              },
            ],
            deleted: [],
            updated: [
              {
                id: 101,
                old: {
                  user_id: 1,
                  amount: 100.0,
                  status: "pending",
                  created_at: "2024-03-01 12:00:00",
                  updated_at: "2024-03-01 12:00:00",
                  tracking_number: null,
                },
                new: {
                  user_id: 1,
                  amount: 100.0,
                  status: "completed",
                  created_at: "2024-03-01 12:00:00",
                  updated_at: "2025-06-28 15:45:00",
                  tracking_number: "TRK789DEF",
                },
                changes: { status: "completed", tracking_number: "TRK789DEF" },
              },
            ],
          },
          products: {
            added: [
              {
                id: 204,
                name: "New Gaming Mouse",
                price: 75.99,
                stock: 100,
                category: "Electronics",
                description: "High-precision gaming mouse.",
                created_at: "2025-06-28 16:00:00",
                updated_at: "2025-06-28 16:00:00",
              },
            ],
            deleted: [
              {
                id: 203,
                name: "Old Product",
                price: 25.0,
                created_at: "2023-11-01 09:00:00",
                updated_at: "2023-11-01 09:00:00",
                stock: 5,
                category: "Obsolete",
              },
            ],
            updated: [
              {
                id: 201,
                old: {
                  name: "Laptop X",
                  price: 1200.0,
                  stock: 50,
                  created_at: "2024-01-10 11:00:00",
                  updated_at: "2024-01-10 11:00:00",
                },
                new: {
                  name: "Laptop X Pro",
                  price: 1250.0,
                  stock: 45,
                  created_at: "2024-01-10 11:00:00",
                  updated_at: "2025-06-28 16:05:00",
                },
                changes: { name: "Laptop X Pro", price: 1250.0, stock: 45 },
              },
            ],
          },
        };

        this.setDiffDataAndExpand(mockDiffData);
      } catch (err) {
        this.error = "Failed to fetch database diff data.";
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.gradient-text {
  background: linear-gradient(to right, #2196f3, #9c27b0);
  /* Blue to Purple */
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  /* Required for gradient-text to work */
}

/* Additional styles for multiple view methods */
.side-by-side-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.side-by-side-column {
  flex: 1;
  padding: 1rem;
  border-radius: 8px;
  background-color: #f9f9f9;
}

/* Styles for top-bottom table view highlighting */
.highlighted-old {
  background-color: #ffcdd2 !important;
  /* Light red for old values */
  border: 2px solid #e57373;
}

.highlighted-new {
  background-color: #e8f5e9 !important;
  border: 2px solid #4caf50 !important;
}

.record-card {
  margin-bottom: 1rem;
  border-radius: 8px;
}

.field-change {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  margin: 0.25rem 0;
  border-radius: 4px;
  background-color: #f5f5f5;
}

.field-change.changed {
  background-color: #fff3cd;
  border-left: 3px solid #ffc107;
}

.view-selector {
  margin-bottom: 1rem;
}

.json-display {
  font-family: "Courier New", monospace;
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-word;
  border: 1px solid #dee2e6;
}

/* Table specific styling for diff visualization */
.diff-table-container {
  overflow-x: auto;
  /* Enable horizontal scrolling for wide tables */
  margin-bottom: 1rem;
  border-radius: 8px;
  /* Match outer card radius */
  border: 1px solid rgba(0, 0, 0, 0.1);
  /* Light border for separation */
  background-color: #fff;
  /* White background for tables */
}

.diff-table {
  width: 100%;
  /* Ensure table takes full width of container */
  border-collapse: collapse;
  /* Remove space between cells */
  font-family: "Fira Code", "Cascadia Code", "Consolas", monospace;
  font-size: 0.85rem;
}

.diff-table th,
.diff-table td {
  padding: 6px 8px;
  /* Reduced padding */
  text-align: left;
  border: 1px solid #e0e0e0;
  /* Light gray borders */
  white-space: nowrap;
  /* Prevent wrapping in cells, allow horizontal scroll */
  cursor: pointer;
  /* Indicate clickable rows/cells */
}

.diff-table th {
  background-color: #f0f0f0;
  /* Lighter background for headers */
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.8em;
  color: #616161;
}

.diff-table tbody tr:nth-child(odd) {
  background-color: #fafafa;
  /* Subtle striping */
}

.diff-table tbody tr:hover {
  background-color: #e3f2fd;
  /* Light blue on hover */
}

/* Highlighting for changed fields within old/new data in table cells */
.diff-table .changed-field-value {
  background-color: #fff9c4;
  /* Soft yellow for highlighting changes */
  font-weight: bold;
  border-radius: 4px;
  padding: 2px 4px;
  display: inline-block;
  /* Ensure padding applies well */
}

/* Styles for the "Changes" JSON block */
.changes-json-block {
  font-family: "Fira Code", "Cascadia Code", "Consolas", monospace;
  background-color: #fff9c4;
  /* yellow lighten-4 */
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 0.85rem;
  white-space: pre-wrap;
  max-height: 40vh;
  /* Adjust max height if needed */
  overflow-y: auto;
}

/* Border for updated records section */
.border-b {
  border-bottom: 1px solid var(--v-grey-lighten2);
}

.last\:border-b-0:last-child {
  border-bottom: none !important;
}

/* Ensure some margin between expansion panels */
.v-expansion-panel + .v-expansion-panel {
  margin-top: 12px;
  /* Adjust as needed */
}

/* Combined table specific styles */
.combined-table {
  font-size: 0.9rem;
}

.value-type-cell {
  font-weight: bold;
  text-align: center;
  min-width: 100px;
  position: sticky;
  left: 0;
  z-index: 1;
}

.value-type-added {
  background-color: #e8f5e9 !important;
  color: #2e7d32 !important;
}

.value-type-deleted {
  background-color: #ffebee !important;
  color: #c62828 !important;
}

.value-type-updated-old {
  background-color: #fff3e0 !important;
  color: #ef6c00 !important;
}

.value-type-updated-new {
  background-color: #e3f2fd !important;
  color: #1976d2 !important;
}

.row-added {
  background-color: #f1f8e9 !important;
}

.row-added:hover {
  background-color: #e8f5e9 !important;
}

.row-deleted {
  background-color: #fce4ec !important;
}

.row-deleted:hover {
  background-color: #ffebee !important;
}

.row-updated-old {
  background-color: #fff8e1 !important;
}

.row-updated-old:hover {
  background-color: #fff3e0 !important;
}

.row-updated-new {
  background-color: #e1f5fe !important;
}

.row-updated-new:hover {
  background-color: #e3f2fd !important;
}

.cell-changed {
  background-color: #fff59d !important;
  font-weight: bold;
  border: 1px solid #fbc02d !important;
}

.copy-button-container {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: white;
  padding: 8px 0;
  border-bottom: 2px solid #e0e0e0;
}
</style>
